// import { useState, useEffect } from 'react';
import { Slider, YesNoToggle } from '@calefy-inc/informedMaterial';
import {
  ArrayField,
  // Debug,
  Relevant,
  // useArrayFieldApi,
  useArrayFieldItemState,
  // useField,
  useArrayFieldItemApi,
  useFieldApi,
  useFormApi,
} from 'informed';
import { Button, Grid, FormHelperText } from '@mui/material';
import RenderedQuestionInstance from './RenderedQuestionInstance';
// import AnimatedRelevant from './AnimatedRelevant';
import { v4 as createId } from 'uuid';
import { useTheme } from '@mui/material/styles';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';

import { formatAsReadableList, valueOrMax } from './utility';

// types and classes
import type { $TSFixMe } from '@calefy-inc/utilityTypes';
import { Language, LanguageAwareString } from '../../../Typescript/classes';
import {
  QuoteComponentProps,
  QuoteComponentValidationFunctionReturn,
} from './types';
import { Component } from './classes';
import { ProgramBuilderQuestionInstance } from '../../FormManager/classes';
import { QuoteWizardQuestionInstance } from '../../QuoteWizard/classes';
import { FormValues } from '../../../Typescript/forms';

export const defaultInternationalRevenuePropsBlob = {
  dataType: '%',
  slider_max: 100,
};
export const defaultSubQuestions = [
  new ProgramBuilderQuestionInstance({
    clientId: createId(),
    apiName: 'international_revenue_bool',
    displayNames: [
      new LanguageAwareString('international_revenue_bool', Language.english),
    ],
    component: 'YesNoToggle',
    labels: [
      new LanguageAwareString(
        'Do you have any revenue from outside of Canada?',
        Language.english,
      ),
    ],
    helpTexts: [
      new LanguageAwareString(
        'The USA counts as outside of Canada.',
        Language.english,
      ),
    ],
    dataType: 'boolean',
    propsBlob: defaultInternationalRevenuePropsBlob,
    subQuestions: [],
  }),
  new ProgramBuilderQuestionInstance({
    clientId: createId(),
    apiName: 'canada',
    displayNames: [new LanguageAwareString('canada', Language.english)],
    component: 'slider',
    labels: [new LanguageAwareString('Canada', Language.english)],
    dataType: 'integer',
    propsBlob: defaultInternationalRevenuePropsBlob,
    required: true,
    subQuestions: [],
  }),
  new ProgramBuilderQuestionInstance({
    clientId: createId(),
    apiName: 'usa',
    displayNames: [new LanguageAwareString('usa', Language.english)],
    labels: [new LanguageAwareString('USA', Language.english)],
    component: 'slider',
    dataType: 'integer',
    propsBlob: defaultInternationalRevenuePropsBlob,
    required: true,
    subQuestions: [],
  }),
  new ProgramBuilderQuestionInstance({
    clientId: createId(),
    apiName: 'international',
    displayNames: [new LanguageAwareString('international', Language.english)],
    labels: [new LanguageAwareString('International', Language.english)],
    component: 'slider',
    dataType: 'integer',
    propsBlob: defaultInternationalRevenuePropsBlob,
    required: true,
    subQuestions: [],
  }),
];

function QuoteComponent({
  questionInstance,
  classes = {},
  keepStateIfRelevant = false,
  ...props
}: QuoteComponentProps) {
  // Filter non-default subquestions
  const nonDefaultSubquestions =
    questionInstance &&
    questionInstance.subQuestions &&
    questionInstance.subQuestions.length > 0
      ? questionInstance.subQuestions.filter(
          (subQuestion: $TSFixMe) =>
            !defaultSubQuestions
              .map((defaultSubQuestion) => defaultSubQuestion.apiName)
              .includes(subQuestion.apiName),
        )
      : [];

  return (
    <ArrayField name={questionInstance.apiName}>
      {({ add, fields }) => {
        if (fields.length === 0) {
          add();
        }
        return (
          <>
            <ArrayField.Items>
              {() => (
                <InternationalRevenueItem
                  questionInstance={questionInstance}
                  classes={classes}
                  nonDefaultSubquestions={nonDefaultSubquestions}
                  keepStateIfRelevant={keepStateIfRelevant}
                  {...props}
                />
              )}
            </ArrayField.Items>
          </>
        );
      }}
    </ArrayField>
  );
}
// Default variables for each slider
const MAX = 100;
const MIN = 0;
const SUFFIX = '%';
const PREFIX = null;

// Seperate component to use hooks inside ArrayField.Items
interface InternationalRevenueItemProps {
  questionInstance: QuoteWizardQuestionInstance;
  nonDefaultSubquestions: Array<QuoteWizardQuestionInstance>;
  classes: $TSFixMe;
  keepStateIfRelevant?: boolean;
  [key: string]: $TSFixMe;
}
const InternationalRevenueItem = ({
  questionInstance,
  classes = {},
  nonDefaultSubquestions,
  keepStateIfRelevant = false,
  ...props
}: InternationalRevenueItemProps) => {
  const theme = useTheme();

  // Use the hook to access array field item state
  const { name } = useArrayFieldItemState();
  const arrayFieldItemApi = useArrayFieldItemApi();
  const formApi = useFormApi();

  const getCountryValues = () => {
    const revenueArray = formApi.getValue(questionInstance.generateFieldName());

    // these values are the first (and only) item in the array, and are in the shape of a json/dictionary object
    const revenueValues = Array.isArray(revenueArray)
      ? revenueArray[0]
      : undefined;

    const canadaValue = getRevenueValue(revenueValues, 'canada');
    const usaValue = getRevenueValue(revenueValues, 'usa');
    const internationalValue = getRevenueValue(revenueValues, 'international');

    return { canadaValue, usaValue, internationalValue };
  };

  const { usaValue, canadaValue, internationalValue } = getCountryValues();

  let sum = [canadaValue, usaValue, internationalValue].reduce((acc, value) => {
    return acc + value;
  }, 0);

  const validateSum = () => {
    const revenueArray = formApi.getValue(questionInstance.generateFieldName());

    // these values are the first (and only) item in the array, and are in the shape of a json/dictionary object
    const revenueValues = Array.isArray(revenueArray)
      ? revenueArray[0]
      : undefined;

    const currentCanada = getRevenueValue(revenueValues, 'canada');
    const currentUsa = getRevenueValue(revenueValues, 'usa');
    const currentInternational = getRevenueValue(
      revenueValues,
      'international',
    );

    const currentSum = currentCanada + currentUsa + currentInternational;

    const returnedValue = currentSum !== 100 ? 'Sum must be 100%' : undefined;

    return returnedValue;
  };

  const validateAllSliders = () => {
    const validation = validateSum();
    if (validation === undefined) {
      // If valid, clear errors on all sliders
      validateCanada();
      validateUSA();
      validateInternational();
    }
  };

  const sourceQuestion = questionInstance.subQuestions[0];

  // Set field prefix using 'name' from the hook
  sourceQuestion.fieldPrefix = name;

  const { validate: validateCanada } = useFieldApi(canadaFieldName);
  const { validate: validateUSA } = useFieldApi(usaFieldName);
  const { validate: validateInternational } = useFieldApi(
    internationalFieldName,
  );

  // Reset slider values to 0
  const resetSliders = () => {
    arrayFieldItemApi.setValue('canada', 0);
    arrayFieldItemApi.setValue('usa', 0);
    arrayFieldItemApi.setValue('international', 0);
  };

  return (
    <>
      {/* <pre>{JSON.stringify(values, null, 4)}</pre> */}
      {/* <Debug values={true} initialValues={true} /> */}
      <YesNoToggle
        name={sourceQuestion.apiName}
        label={sourceQuestion.getQuoteQuestionLabel()}
      />
      <Relevant
        when={
          // @ts-expect-error
          ({ formApi }) => {
            if (!arrayFieldItemApi || !sourceQuestion) {
              return false;
            }
            // @ts-expect-error
            const sourceValue = arrayFieldItemApi.getValue(
              sourceQuestion.apiName,
            );
            return sourceValue === 'yes';
          }
        }
      >
        <>
          <div className={classes.slidergroup}>
            <Slider
              label='What percentage of your revenue is from Canada?'
              name={canadaFieldName}
              className={classes.slider}
              classes={classes}
              size='small'
              max={MAX}
              min={MIN}
              validate={validateSum}
              validateOn={'submit'}
              prefix={PREFIX}
              suffix={SUFFIX}
              required={true}
              onChange={
                // @ts-expect-error
                ({ value, previousValue }) => {
                  // Skip if value is undefined (initialization events)
                  if (value === undefined) {
                    return;
                  }

                  const valueAsNumber = Number(value);

                  const { usaValue, canadaValue, internationalValue } =
                    getCountryValues();

                  // Calculate sum of all sliders (including current Canada value)
                  const currentSum =
                    canadaValue + usaValue + internationalValue;

                  // Use valueOrMax to calculate the clamped value
                  const clampedValue = valueOrMax(
                    valueAsNumber,
                    canadaValue,
                    currentSum,
                  );

                  // If value exceeds limit, force it back to the clamped value
                  if (valueAsNumber !== clampedValue) {
                    // Use setTimeout to break out of current event cycle
                    setTimeout(() => {
                      arrayFieldItemApi.setValue('canada', clampedValue);
                    }, 0);
                  }
                }
              }
              onBlur={() => {
                validateAllSliders();
              }}
              style={{
                marginTop: 0,
                marginBottom: 0,
              }}
              {...props}
            />
            <Slider
              label='What percentage of your revenue is from the USA?'
              name={usaFieldName}
              className={classes.slider}
              classes={classes}
              max={MAX}
              min={MIN}
              size='small'
              validate={validateSum}
              validateOn={'submit'}
              prefix={PREFIX}
              suffix={SUFFIX}
              required={true}
              onChange={
                // @ts-expect-error
                ({ value, previousValue }) => {
                  // Skip if value is undefined (initialization events)
                  if (value === undefined) {
                    return;
                  }

                  const valueAsNumber = Number(value);

                  const { usaValue, canadaValue, internationalValue } =
                    getCountryValues();

                  // Calculate sum of all sliders (including current USA value)
                  const currentSum =
                    canadaValue + usaValue + internationalValue;

                  // Use valueOrMax to calculate the clamped value
                  const clampedValue = valueOrMax(
                    valueAsNumber,
                    usaValue,
                    currentSum,
                  );

                  // If value exceeds limit, force it back to the clamped value
                  if (valueAsNumber !== clampedValue) {
                    // Use setTimeout to break out of current event cycle
                    setTimeout(() => {
                      arrayFieldItemApi.setValue('usa', clampedValue);
                    }, 0);
                  }
                }
              }
              style={{
                marginTop: 0,
                marginBottom: 0,
              }}
              onBlur={() => {
                validateAllSliders();
              }}
              {...props}
            />
            <Slider
              label='What percentage of your revenue is from outside Canada and the USA?'
              name={internationalFieldName}
              className={classes.slider}
              classes={classes}
              max={MAX}
              min={MIN}
              size='small'
              validate={validateSum}
              validateOn={'submit'}
              prefix={PREFIX}
              suffix={SUFFIX}
              required={true}
              onChange={
                // @ts-expect-error
                ({ value, previousValue }) => {
                  // Skip if value is undefined (initialization events)
                  if (value === undefined) {
                    return;
                  }

                  const valueAsNumber = Number(value);

                  const { usaValue, canadaValue, internationalValue } =
                    getCountryValues();

                  // Calculate sum of all sliders (including current International value)
                  const currentSum =
                    canadaValue + usaValue + internationalValue;

                  // Use valueOrMax to calculate the clamped value
                  const clampedValue = valueOrMax(
                    valueAsNumber,
                    internationalValue,
                    currentSum,
                  );

                  // If value exceeds limit, force it back to the clamped value
                  if (valueAsNumber !== clampedValue) {
                    // Use setTimeout to break out of current event cycle
                    setTimeout(() => {
                      arrayFieldItemApi.setValue('international', clampedValue);
                    }, 0);
                  }
                }
              }
              style={{
                marginTop: 0,
                marginBottom: 2,
              }}
              onBlur={() => {
                validateAllSliders();
              }}
              {...props}
            />
            <div className={classes.util}>
              <Grid container spacing={3}>
                <Grid item xs={10}>
                  <Button
                    size='medium'
                    type='button'
                    onClick={() => {
                      resetSliders();
                    }}
                  >
                    Reset
                  </Button>
                </Grid>

                <Grid item xs={2}>
                  <label>
                    <span
                      style={
                        sum && sum >= 100
                          ? {
                              color: theme.palette.primary.main,
                              fontWeight: 'bold',
                            }
                          : undefined
                      }
                    >
                      Sum: {sum}%
                    </span>
                  </label>
                </Grid>
              </Grid>
            </div>
            <FormHelperText>Sum of sliders must be 100%</FormHelperText>
          </div>
          {nonDefaultSubquestions.map((subQuestion) => {
            subQuestion.fieldPrefix = name;
            return (
              <RenderedQuestionInstance
                key={subQuestion.apiName}
                questionInstance={subQuestion}
                keepStateIfRelevant={keepStateIfRelevant}
              />
            );
          })}
        </>
      </Relevant>
    </>
  );
};

const canadaFieldName = 'canada';
const usaFieldName = 'usa';
const internationalFieldName = 'international';

const getRevenueValue = (values: FormValues, apiName: string) => {
  const raw = values?.[apiName];
  const asNumber = Number(raw);
  return !Number.isNaN(asNumber) && Number.isFinite(asNumber) ? asNumber : 0;
};

function gatherSubQuestions(
  _formState: $TSFixMe,
  questionInstance: ProgramBuilderQuestionInstance,
) {
  return initialSubQuestions(questionInstance);
}

function initialSubQuestions(questionInstance: ProgramBuilderQuestionInstance) {
  questionInstance.subQuestions = [
    ...defaultSubQuestions,
    ...questionInstance.subQuestions.filter(
      (subq) =>
        !defaultSubQuestions
          .map((defaultSubQ) => defaultSubQ.apiName)
          .includes(subq.apiName),
    ),
  ];
  questionInstance.children = questionInstance.subQuestions.map((subq) =>
    subq.copy(),
  );
  return questionInstance;
}

function validateInternationalRevenue(
  questionInstance: ProgramBuilderQuestionInstance,
): QuoteComponentValidationFunctionReturn {
  const errorMessages = [];
  const requiredSubquestionApiNames = defaultSubQuestions.map((q) => q.apiName);
  const missingComponentApiNames = requiredSubquestionApiNames.filter(
    (apiName) =>
      !questionInstance.subQuestions.find((subq) => subq.apiName === apiName),
  );
  if (missingComponentApiNames.length > 0) {
    const plural = missingComponentApiNames.length > 1;
    errorMessages.push(
      `International Revenue component with name "${questionInstance.generateDisplayName()}" is missing ${
        plural ? '' : 'a '
      }required subquestion${plural ? 's' : ''} with API name${
        plural ? 's' : ''
      } ${formatAsReadableList(
        missingComponentApiNames.map((s) => `"${s}"`),
      )}. The safest thing to do is to delete and re-create it to avoid problems later.`,
    );
  }
  return errorMessages;
}

const IconComponent = () => {
  return <PublicRoundedIcon fontSize='small' color='primary' />;
};

export default function createComponentRegistry() {
  return new Component({
    type: 'internationalRevenue',
    gatherSubQuestions: gatherSubQuestions,
    typeLabel: 'International Revenue Check',
    quoteComponent: QuoteComponent,
    dataType: 'nested',
    programBuilderValidation: validateInternationalRevenue,
    icon: IconComponent,
  });
}

export { QuoteComponent };
